import React, { useState } from 'react';

const StepDepensesMensuelles = ({ formData, onChange }) => {
  const [showCreditConfirmation, setShowCreditConfirmation] = useState(false);
  const [showCreditFields, setShowCreditFields] = useState(
    (parseFloat(formData.creditConso) || 0) > 0
  );
  
  // Nouvel état pour suivre quels champs ont "Aucun" sélectionné
  const [aucunSelected, setAucunSelected] = useState({
    loyerActuel: false,
    remboursementSecondaire: false,
    creditConso: false,
    pensionsVersees: false
  });
  
  // Gestionnaire pour les inputs numériques
  const handleNumericChange = (e) => {
    const { name, value } = e.target;
    
    // Si une valeur est entrée, désactiver le bouton "Aucun" pour ce champ
    if (value !== '') {
      setAucunSelected(prev => ({
        ...prev,
        [name]: false
      }));
    }
    
    onChange({ target: { name, value: value === '' ? '' : value } });
    
    // Si c'est un champ de crédit, vérifier s'il faut montrer les champs supplémentaires
    if (name === 'creditConso') {
      const numValue = parseFloat(value) || 0;
      if (numValue > 0) {
        setShowCreditFields(true);
      } else {
        setShowCreditFields(false);
        setShowCreditConfirmation(true);
      }
    }
  };
  
  // Gestionnaire pour les boutons "Aucun"
  const handleAucunClick = (fieldName) => {
    // Inverser l'état du bouton "Aucun" pour ce champ
    const newState = !aucunSelected[fieldName];
    
    setAucunSelected(prev => ({
      ...prev,
      [fieldName]: newState
    }));
    
    // Si on active "Aucun", effacer la valeur du champ
    if (newState) {
      onChange({ target: { name: fieldName, value: '' } });
      
      // Si c'est un champ de crédit, mettre à jour l'affichage
      if (fieldName === 'creditConso') {
        setShowCreditFields(false);
      }
    }
  };
  
  // Confirmation d'absence de crédit
  const confirmNoCredit = () => {
    setShowCreditConfirmation(false);
  };

  // Fonction pour rendre un champ monétaire avec bouton "Aucun"
  const renderMoneyField = (name, label, placeholder = "0") => {
    return (
      <div>
        <label className="block text-sm font-medium mb-1 text-orpi-secondary">
          {label} <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center space-x-2">
          <div className="relative flex-grow">
            <input
              type="number"
              name={name}
              value={formData[name] || ''}
              onChange={handleNumericChange}
              className={`w-full p-3 border rounded-lg pr-12 focus:border-orpi-primary focus:ring-orpi-primary ${
                aucunSelected[name] ? 'bg-gray-50' : ''
              }`}
              placeholder={placeholder}
              min="0"
              required={!aucunSelected[name]}
            />
            <span className="absolute right-3 top-3 text-gray-500">€ / mois</span>
          </div>
          <button
            type="button"
            onClick={() => handleAucunClick(name)}
            className={`px-3 py-2 rounded-lg border ${
              aucunSelected[name] 
                ? 'bg-orpi-light text-orpi-primary border-orpi-primary' 
                : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Aucun
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-orpi-secondary">
        Quelles sont vos dépenses mensuelles ?
      </h2>

      <p className="text-gray-600 mb-6">
        Veuillez déclarer les charges que vous payez chaque mois dans leur globalité.
      </p>

      {/* Message de confirmation d'absence de crédit */}
      {showCreditConfirmation && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-r">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Confirmation d'absence de crédit
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Merci de confirmer votre réponse avec certitude. Vous avez indiqué ne pas avoir de crédit en cours.</p>
                <p className="mt-2">Nous vous rappelons qu'il est essentiel de vérifier cette information avant de la valider. Si vous avez d'autres crédits en cours, à l'exception du crédit immobilier pour votre résidence principale (le cas échéant), veuillez les déclarer avec précision.</p>
                <p className="mt-2">Toutes les informations fournies dans ce formulaire seront vérifiées pour garantir la cohérence et l'exactitude de votre dossier. Une omission ou une incohérence détectée pourrait entraîner un refus ou retarder le traitement de votre demande.</p>
                <p className="mt-2">Si vous avez un doute ou si vous souhaitez rectifier votre réponse, nous vous invitons à revenir à la question précédente pour mettre à jour vos informations.</p>
              </div>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={confirmNoCredit}
                  className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200"
                >
                  Je confirme n'avoir aucun crédit en cours
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Loyer / remboursement résidence principale */}
        {renderMoneyField('loyerActuel', 'Loyer / remboursement résidence principale')}

        {/* Remboursement résidence secondaire */}
        {renderMoneyField('remboursementSecondaire', 'Remboursement résidence secondaire')}

        {/* Crédits conso en cours */}
        {renderMoneyField('creditConso', 'Crédits conso en cours (Cetelem, Cofidis, Floa, etc.)')}

        {/* Champs supplémentaires pour les crédits si montant > 0 */}
        {showCreditFields && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-medium mb-3 text-orpi-secondary">
              Précisez le nom du ou des prêteurs <span className="text-red-500">*</span>
            </label>
            <textarea
              name="nomPreteurs"
              value={formData.nomPreteurs || ''}
              onChange={(e) => onChange(e)}
              className="w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary"
              placeholder="Ex: Cetelem, Cofidis, Banque Populaire..."
              rows="3"
              required
            ></textarea>
          </div>
        )}

        {/* Pension(s) alimentaire(s) versée(s) */}
        {renderMoneyField('pensionsVersees', 'Pension(s) alimentaire(s) versée(s)')}

        {/* Séparateur avec informations bancaires */}
        <div className="border-t border-gray-200 pt-6 mt-8"></div>

        {/* Banque de réception du salaire */}
        <div>
          <label className="block text-sm font-medium mb-1 text-orpi-secondary">
            Banque où vous recevez votre salaire <span className="text-red-500">*</span>
          </label>
          <select
            name="banque"
            value={formData.banque || ''}
            onChange={(e) => onChange(e)}
            className="w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary"
            required
          >
            <option value="">Sélectionnez votre banque</option>
            <option value="bnp">BNP Paribas</option>
            <option value="sg">Société Générale</option>
            <option value="ca">Crédit Agricole</option>
            <option value="lcl">LCL</option>
            <option value="bp">Banque Populaire</option>
            <option value="ce">Caisse d'Épargne</option>
            <option value="cm">Crédit Mutuel</option>
            <option value="ing">ING</option>
            <option value="boursorama">Boursorama</option>
            <option value="fortuneo">Fortuneo</option>
            <option value="n26">N26</option>
            <option value="revolut">Revolut</option>
            <option value="hellobank">Hello Bank</option>
            <option value="autre">Autre banque</option>
          </select>
        </div>

        {formData.banque === 'autre' && (
          <div>
            <label className="block text-sm font-medium mb-1 text-orpi-secondary">
              Précisez le nom de votre banque <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="autreBanque"
              value={formData.autreBanque || ''}
              onChange={(e) => onChange(e)}
              className="w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary"
              placeholder="Nom de votre banque"
              required
            />
          </div>
        )}

        {/* Année d'ouverture du compte */}
        <div>
          <label className="block text-sm font-medium mb-1 text-orpi-secondary">
            Année d'ouverture du compte <span className="text-red-500">*</span>
          </label>
          <select
            name="anneeOuvertureCompte"
            value={formData.anneeOuvertureCompte || ''}
            onChange={(e) => onChange(e)}
            className="w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary"
            required
          >
            <option value="">Sélectionnez l'année</option>
            {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i).map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>
      
      <p className="text-sm text-gray-500 mt-6">
        <span className="text-red-500">*</span> Champs obligatoires
      </p>
    </div>
  );
};

export default StepDepensesMensuelles;